import { useRef, useLayoutEffect } from 'react';
import { css, etiColors, hexToRgba, mediaQueries } from '@eti/styles';
import { Radio } from '@eti/form';
import Media from 'react-media';
import { Icon as EtiIcon, DangerousHtmlContent } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { Tier } from '@eti/schema-types';
import { getPriceDivided as getPricePerTraveler } from '../../../common/utils/price';
import { formatOriginalPrice, formatPrice, orderBundles } from '../../utils/utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const typeColors = type => {
  if (type === Tier.Basic) {
    return etiColors.grayScale[8];
  }
  if (type === Tier.Recommended) {
    return etiColors.confirmation.solid;
  }
  if (type === Tier.Premium) {
    return etiColors.info.solid;
  }
  return etiColors.info.solid;
};
const getColor = (type, selected) => hexToRgba(typeColors(type), selected ? 1 : 0.4);
const productBundleItemWrapperStyles = (type, totalItems) => css`
  border: 1px solid ${getColor(type, false)};
  border-radius: 3px;
  box-shadow: 0 3px 6px #00000029;
  display: flex;
  flex: 0 auto;
  flex-direction: column;
  margin-bottom: 14px;
  order: ${orderBundles(type).mobileOrder};
  padding: 10px 10px 10px 15px;
  position: relative;

  &::before {
    background-color: ${getColor(type, false)};
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 5px;

    [dir='rtl'] & {
      left: initial;
      right: 0;
    }
  }

  &:last-child {
    margin-bottom: 14px;
  }

  [dir='rtl'] & {
    padding-left: 10px;
    padding-right: 15px;
  }

  @media ${mediaQueries.medium.up} {
    flex: 0 1 25%;
    margin: 0 ${60 / totalItems - 10}px;
    order: ${orderBundles(type).desktopOrder};
    padding-top: 15px;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-bottom: 0;
      margin-right: 0;
    }

    [dir='rtl'] & {
      &:first-child {
        margin-left: ${60 / totalItems - 10}px;
        margin-right: 0;
      }

      &:last-child {
        margin-left: 0;
        margin-right: ${60 / totalItems - 10}px;
      }
    }
  }
`;
const ribbonTextContainerStyles = css`
  position: absolute;
  right: 8px;
  top: -13px;

  [dir='rtl'] & {
    left: 8px;
    right: initial;
  }

  @media ${mediaQueries.medium.up} {
    right: 0;
    text-align: center;
    top: -12px;
    width: 100%;
  }
`;
const ribbonTextStyles = type => css`
  background-color: ${typeColors(type)};
  border-radius: 2px;
  color: ${etiColors.white};
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
`;
const titleWrapperStyles = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-top: 0;

  @media ${mediaQueries.medium.up} {
    align-items: flex-start;
    flex-direction: column;
    padding-top: 0;
  }
`;
const titleStyles = css`
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;

  @media ${mediaQueries.medium.up} {
    font-size: 1.375rem;
  }
`;
const priceWrapperStyles = css`
  display: flex;

  @media ${mediaQueries.medium.up} {
    border-top: 1px solid ${etiColors.grayScale[5]};
    margin-bottom: 5px;
    margin-top: 10px;
    padding-top: 5px;
    width: 100%;
  }
`;
const priceContentStyles = css`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  margin-right: 5px;

  [dir='rtl'] & {
    margin-left: 5px;
    margin-right: 0;
  }

  @media ${mediaQueries.medium.up} {
    align-items: flex-start;
  }
`;
const priceSummaryStyles = css`
  align-items: flex-end;
  display: flex;
  margin-right: 5px;

  [dir='rtl'] & {
    margin-left: 5px;
    margin-right: 0;
  }

  @media ${mediaQueries.medium.up} {
    align-items: flex-start;
  }
`;
const originalPriceStyles = css`
  color: ${etiColors.grayScale[6]};
  font-size: 0.875rem;
  font-weight: 700;
  margin-right: 8px;
  text-decoration: line-through;
  white-space: nowrap;

  [dir='rtl'] & {
    margin-left: 8px;
    margin-right: 0;
  }

  @media ${mediaQueries.medium.up} {
    font-size: 1.125rem;
    margin-left: 8px;
    max-height: 35px;
    order: 2;
    padding-top: 4px;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 8px;
    }
  }
`;
const priceStyles = css`
  font-size: 0.875rem;
  font-weight: 700;
  order: 2;
  white-space: nowrap;

  @media ${mediaQueries.medium.up} {
    font-size: 1.375rem;
  }
`;
const priceTextStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.625rem;
  text-align: right;

  [dir='rtl'] & {
    text-align: left;
  }
`;
const featuresListStyles = isExpanded => css`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-top: ${isExpanded ? '10px' : 0};
  max-height: ${isExpanded ? '1000px' : '1px'};
  opacity: ${isExpanded ? '100%' : 0};
  overflow: hidden;
  transition: 0.3s;

  @media ${mediaQueries.medium.up} {
    margin-bottom: 20px;
    margin-top: 0;
    max-height: 1000px;
    opacity: 1;
  }
`;
const featureStyles = isIncluded => css`
  align-items: flex-start;
  border-top: 1px solid ${etiColors.grayScale[5]};
  color: ${isIncluded ? etiColors.black : etiColors.grayScale[9]};
  display: flex;
  font-size: 0.875rem;
  padding: 14px 14px 14px 0;

  [dir='rtl'] & {
    padding-left: 14px;
    padding-right: 0;
  }
`;
const ribbonStyles = selected => css`
  display: none;
  opacity: ${selected ? 1 : 0.4};
  position: absolute;
  top: 0;

  [dir='rtl'] & {
    left: 0;
    right: initial;
  }

  @media ${mediaQueries.medium.up} {
    display: block;
    right: 0;
  }
`;
const toggleIconStyles = isExpanded => css`
  transform: ${`rotate(${isExpanded ? '180deg' : 0})`};
  transition: 0.2s;

  @media ${mediaQueries.medium.up} {
    display: none;
  }
`;
const featureIconStyles = css`
  margin-right: 10px;

  [dir='rtl'] & {
    margin-left: 10px;
    margin-right: 0;
  }
`;
const infoIconStyles = css`
  align-self: center;
  cursor: pointer;
  margin-left: 4.8px;
  max-height: 35px;
  width: 16px;

  html[dir='rtl'] & {
    margin-left: 0;
    margin-right: 4.8px;
  }
`;
const emptyFeatureStyles = css`
  ${featureStyles(false)}
  color: ${etiColors.black};
  padding-left: 22px;

  [dir='rtl'] & {
    padding-right: 22px;
  }
`;
const selectButtonStyles = css`
  border: 1px solid ${etiColors.grayScale[7]};
  border-radius: 3px;
  padding: 12px;
  width: 100%;

  .etiCustomRadio {
    left: 10px;
    top: 14px;

    [dir='rtl'] & {
      left: initial;
      right: 10px;
    }
  }
`;
const mobileRadioStyles = css`
  display: flex;
`;
const buttonTextStyles = css`
  color: ${etiColors.black};
  font-size: 0.875rem;
  margin-right: 16px;
  padding: 0 10px;
  text-align: center;

  [dir='rtl'] & {
    margin-left: 16px;
    margin-right: 0;
  }
`;
const discountTextStyles = (type, selected) => css`
  color: ${getColor(type, selected)};
  font-weight: 800;
  margin-bottom: 5px;
`;
const getFeatureId = _ref => {
  let {
    index,
    bundleName
  } = _ref;
  return `${bundleName}-feature-${index}`;
};
const ProductBundleItem = _ref2 => {
  let {
    bundleName,
    buttonText,
    features = [],
    id,
    isExpanded,
    priceRaw,
    priceText,
    ribbonText,
    noExtrasText,
    isSelected,
    title,
    toggleItem,
    type,
    totalItems,
    openInfoModal = () => {},
    shouldShowPricePerPerson = false,
    travelers = [],
    originalPrice,
    discountPercentagePackageText
  } = _ref2;
  const {
    t
  } = useTranslation();
  const ref = useRef(null);
  const numberOfTravelers = travelers.filter(traveler => traveler.ageType !== 'INF').length;
  const priceToDisplay = shouldShowPricePerPerson ? formatPrice(getPricePerTraveler(priceRaw, numberOfTravelers)) : formatPrice(priceRaw);
  const originalPriceToDisplay = shouldShowPricePerPerson ? formatOriginalPrice(getPricePerTraveler(originalPrice, numberOfTravelers)) : formatOriginalPrice(originalPrice);
  useLayoutEffect(() => {
    let timeout;
    if (isSelected) {
      timeout = setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 300);
    }
    return () => clearTimeout(timeout);
  }, [isSelected]);
  const handleClick = () => {
    toggleItem(type);
  };
  return /*#__PURE__*/_jsxs("div", {
    className: productBundleItemWrapperStyles(type, totalItems),
    "data-testid": `productBundleItem-${id}`,
    ref: ref,
    children: [/*#__PURE__*/_jsx(EtiIcon, {
      className: ribbonStyles(isSelected),
      color: typeColors(type),
      id: "ribbon",
      size: "44px"
    }), ribbonText && /*#__PURE__*/_jsx("div", {
      className: ribbonTextContainerStyles,
      children: /*#__PURE__*/_jsx("span", {
        className: ribbonTextStyles(type),
        children: ribbonText
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: titleWrapperStyles,
      children: [/*#__PURE__*/_jsx(Media, {
        query: `${mediaQueries.medium.up}`,
        children: matches => matches ? /*#__PURE__*/_jsxs("h2", {
          className: titleStyles,
          children: [/*#__PURE__*/_jsx("span", {
            children: title
          }), /*#__PURE__*/_jsx(EtiIcon, {
            className: infoIconStyles,
            color: etiColors.grayScale[6],
            id: "info-circle",
            onClick: openInfoModal
          })]
        }) : /*#__PURE__*/_jsxs(Radio, {
          className: mobileRadioStyles,
          dataTestId: `choice.${bundleName}.${id}`,
          id: `choice.${bundleName}.${id}`,
          isNeutral: false,
          value: type,
          children: [/*#__PURE__*/_jsx("h2", {
            className: titleStyles,
            children: /*#__PURE__*/_jsx("span", {
              children: title
            })
          }), /*#__PURE__*/_jsx(EtiIcon, {
            className: infoIconStyles,
            color: etiColors.grayScale[6],
            id: "info-circle",
            onClick: openInfoModal
          })]
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: priceWrapperStyles,
        onClick: handleClick,
        role: "presentation",
        children: [/*#__PURE__*/_jsxs("div", {
          className: priceContentStyles,
          children: [/*#__PURE__*/_jsxs("div", {
            className: priceSummaryStyles,
            children: [/*#__PURE__*/_jsx("h3", {
              className: priceStyles,
              children: priceToDisplay
            }), originalPrice > 0 && /*#__PURE__*/_jsx("h3", {
              className: originalPriceStyles,
              children: originalPriceToDisplay
            })]
          }), /*#__PURE__*/_jsx("p", {
            className: priceTextStyles,
            children: priceText
          })]
        }), /*#__PURE__*/_jsx(EtiIcon, {
          className: toggleIconStyles(isExpanded),
          color: etiColors.grayScale[10],
          id: "chevron-down",
          size: "16px"
        })]
      })]
    }), /*#__PURE__*/_jsx("ul", {
      className: featuresListStyles(isExpanded),
      children: features.length > 0 ? features.map((_ref3, index) => {
        let {
          isIncluded,
          name,
          weight,
          weightUnit,
          pieces
        } = _ref3;
        return /*#__PURE__*/_jsxs("li", {
          className: featureStyles(isIncluded),
          children: [/*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(EtiIcon, {
              className: featureIconStyles,
              color: isIncluded ? etiColors.confirmation.solid : etiColors.grayScale[8],
              id: isIncluded ? 'check' : 'dash',
              size: "12px"
            })
          }), weight && weightUnit && pieces ? /*#__PURE__*/_jsx(DangerousHtmlContent, {
            content: `${name} ${t('Cart.CabinBaggage.Included.PiecesAndWeight', pieces, weight, weightUnit)}`
          }) : name]
        }, getFeatureId({
          index,
          bundleName
        }));
      }) : /*#__PURE__*/_jsx("div", {
        className: emptyFeatureStyles,
        children: noExtrasText
      })
    }), /*#__PURE__*/_jsx(Media, {
      query: `${mediaQueries.medium.up}`,
      children: matches => matches ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [type !== Tier.Basic && /*#__PURE__*/_jsx("p", {
          className: discountTextStyles(type, true),
          children: discountPercentagePackageText
        }), /*#__PURE__*/_jsx(Radio, {
          className: selectButtonStyles,
          dataTestId: `choice.${bundleName}.${id}`,
          id: `choice.${bundleName}.${id}`,
          isNeutral: false,
          value: type,
          children: /*#__PURE__*/_jsx("div", {
            className: buttonTextStyles,
            children: buttonText
          })
        })]
      }) : null
    })]
  });
};
export default ProductBundleItem;
//# sourceMappingURL=ProductBundleItem.js.map